import { useState } from "react";
import { Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const EMPTY = { name: "", email: "", phone: "", business: "", message: "" };

// service: { key, name } when opened after a paid purchase; controlled via open/onOpenChange (optional)
export const InfoRequestDialog = ({ service = null, open: openProp, onOpenChange, showTrigger = true }) => {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = onOpenChange ?? setOpenState;
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/services/info-request", { ...form, service: service?.key || "" });
      toast.success("Richiesta inviata! Ti abbiamo mandato un'email di conferma.");
      setForm(EMPTY);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const title = service ? "Grazie per l'acquisto!" : "Richiedi informazioni";
  const desc = service
    ? `Hai acquistato "${service.name}". Lasciaci i tuoi dati e le tue indicazioni: ti contatteremo direttamente per iniziare.`
    : "Compila il modulo: riceverai un preventivo per i gadget QR Code via email.";
  const placeholder = service
    ? "Raccontaci la tua attività, cosa ti serve, tempistiche, link o riferimenti utili *"
    : "Quali gadget ti interessano? Quantità, tipologia, ecc. *";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {showTrigger && (
        <DialogTrigger asChild>
          <Button className="rounded-full h-11 px-6 bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold active:scale-95 transition-transform shrink-0" data-testid="gadget-contact">
            <Mail className="h-4 w-4 mr-2" /> Richiedi informazioni
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-md" data-testid={service ? "service-contact-dialog" : "info-request-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{title}</DialogTitle>
          <DialogDescription>{desc}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input required placeholder="Nome e cognome *" value={form.name} onChange={set("name")} data-testid="info-name" />
          <Input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} data-testid="info-email" />
          <Input placeholder="Telefono" value={form.phone} onChange={set("phone")} data-testid="info-phone" />
          <Input placeholder="Nome attività" value={form.business} onChange={set("business")} data-testid="info-business" />
          <Textarea required rows={4} placeholder={placeholder} value={form.message} onChange={set("message")} data-testid="info-message" />
          <Button type="submit" disabled={busy} className="w-full rounded-full h-11 bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold" data-testid="info-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Invia richiesta"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
